import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  GraduationCap,
  Play,
  RotateCcw,
  Target,
  Users,
} from "lucide-react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { cn } from "@/utils/utils";
import {
  isEnrollmentActive,
  useCourseViewer,
} from "@/modules/learningCenter/features/CourseDirectory/hooks/useCourseViewer/useCourseViewer";
import { levelBadgeClasses } from "@/modules/learningCenter/features/CourseDirectory/utils/level";
import { ViewerModule } from "@/modules/learningCenter/features/CourseDirectory/types/viewer";
import {
  buildModuleStatusMap,
  countCompleted,
  formatProgressPercent,
  resolveResumeModuleUuid,
} from "@/modules/learningCenter/features/CourseDirectory/utils/progress";

// Human label describing the kinds of content blocks in a module, used in the
// module nav. Falls back to a block count when the module is empty.
function blockTypeLabel(module: ViewerModule): string {
  if (module.blocks.length === 0) {
    return "No content";
  }
  const types = new Set(module.blocks.map((block) => block.type));
  if (types.size === 1) {
    const [type] = [...types];
    const labels: Record<string, string> = {
      text: "Reading",
      video: "Video",
      image: "Image",
      file: "Resource",
    };
    return labels[type] ?? "Lesson";
  }
  return "Mixed content";
}

export function CourseDetail() {
  const { uuid = "" } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { viewer, isLoading, isMutating, enroll, unenroll, reactivate } =
    useCourseViewer(uuid);

  const [confirmUnenrollOpen, setConfirmUnenrollOpen] = useState(false);

  const statusMap = useMemo(
    () => buildModuleStatusMap(viewer?.moduleProgress ?? []),
    [viewer?.moduleProgress],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center py-24">
        <LoadingIndicator isFullScreen={false} />
      </div>
    );
  }

  if (!viewer) {
    return (
      <div className="flex min-h-full items-center justify-center py-24 text-center text-gray-500 dark:text-gray-400">
        Course not found.
      </div>
    );
  }

  const enrollment = viewer.enrollment;
  const active = isEnrollmentActive(enrollment);
  const cancelled = enrollment?.status === "cancelled";
  const isCompleted = enrollment?.status === "completed";
  const totalModules = viewer.modules.length;
  const completedCount = countCompleted(statusMap);
  const percent = enrollment
    ? formatProgressPercent(enrollment.progressPercentage)
    : 0;

  const firstModuleUuid = viewer.modules[0]?.uuid;
  const resumeModuleUuid = resolveResumeModuleUuid(viewer);

  const goToPlayer = (moduleUuid: string | undefined) => {
    if (moduleUuid) {
      navigate(`/hub/learning-center/courses/${uuid}/modules/${moduleUuid}`);
    }
  };

  const handleEnroll = () => {
    void enroll().then(() => goToPlayer(firstModuleUuid));
  };

  const renderCta = () => {
    if (active) {
      return (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => goToPlayer(resumeModuleUuid)} size="lg">
            {isCompleted ? (
              <>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                Completed
              </>
            ) : (
              <>
                <Play className="mr-1.5 h-4 w-4" />
                {completedCount > 0 ? "Continue Learning" : "Get Started"}
              </>
            )}
          </Button>
          <Button
            disabled={isMutating}
            onClick={() => setConfirmUnenrollOpen(true)}
            variant="outline"
          >
            Cancel Enrollment
          </Button>
        </div>
      );
    }
    if (cancelled) {
      return (
        <Button
          disabled={isMutating}
          onClick={() => void reactivate()}
          size="lg"
        >
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Re-enroll
        </Button>
      );
    }
    // Unenrolled: gate — only Enroll is offered, no way to start the player.
    return (
      <Button disabled={isMutating} onClick={handleEnroll} size="lg">
        <Play className="mr-1.5 h-4 w-4" />
        Enroll
      </Button>
    );
  };

  return (
    <div className="min-h-full">
      {/* Hero banner — full-bleed course image above the header, when set. */}
      {viewer.heroBannerUrl && (
        <div className="w-full overflow-hidden bg-gray-100 dark:bg-slate-800">
          <img
            alt={viewer.title}
            className="h-44 w-full object-cover sm:h-56 lg:h-64"
            src={viewer.heroBannerUrl}
          />
        </div>
      )}

      {/* Full-bleed header band — the band background spans the full content
          width, while its inner content shares the page's centered rails so
          the title lines up with the body below on wide monitors. */}
      <header className="border-b border-gray-200 bg-white py-8 dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto max-w-5xl space-y-4 px-4 sm:px-6 lg:max-w-6xl lg:px-8 xl:max-w-7xl">
          <button
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            onClick={() => navigate("/hub/learning-center/courses")}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Course Library
          </button>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 space-y-3">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {viewer.title}
              </h1>
              {viewer.tagline && (
                <p className="text-base text-gray-500 dark:text-gray-400">
                  {viewer.tagline}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3">
                {viewer.level && (
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                      levelBadgeClasses(viewer.level),
                    )}
                  >
                    {viewer.level}
                  </span>
                )}
                {viewer.estimatedHours && (
                  <span className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="h-4 w-4" />
                    {viewer.estimatedHours} hours
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <BookOpen className="h-4 w-4" />
                  {totalModules} {totalModules === 1 ? "module" : "modules"}
                </span>
              </div>
              {active && (
                <div className="flex items-center gap-3 pt-1">
                  <Progress className="h-2 w-48" value={percent} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {percent}% Complete
                  </span>
                </div>
              )}
            </div>
            <div className="shrink-0">{renderCta()}</div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:max-w-6xl lg:px-8 xl:max-w-7xl">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* About This Course — single card containing overview, objectives,
                a nested "At a Glance" inset, the module guide, and footer CTAs. */}
            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
              {/* Header strip — neutral slate (mirrors labs' slate strip), with a
                  blue book tile (labs' About tile). */}
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 p-5 dark:border-slate-700 dark:from-slate-800 dark:to-slate-700/60">
                <div className="rounded-lg bg-blue-500 p-2">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    About This Course
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Course overview and learning objectives
                  </p>
                </div>
              </div>

              <div className="space-y-6 p-6">
                {(viewer.courseOverview || viewer.description) && (
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Course Overview
                    </h3>
                    <div className="prose prose-sm dark:prose-invert mt-2 max-w-none text-base leading-relaxed text-gray-600 dark:text-gray-300">
                      <ReactMarkdown>
                        {viewer.courseOverview ?? viewer.description ?? ""}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {viewer.learningObjectives.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      You&apos;ll Learn
                    </h3>
                    <ul className="mt-2 space-y-2">
                      {viewer.learningObjectives.map((objective, index) => (
                        <li
                          className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
                          key={index}
                        >
                          <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500 dark:text-emerald-400" />
                          <span>{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Course At a Glance — nested inset sub-box (slightly darker
                    shade than the card body). */}
                {(viewer.targetAudience || viewer.level) && (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-slate-700 dark:bg-slate-900/40">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Course At a Glance
                    </h3>
                    <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {viewer.targetAudience && (
                        <div className="flex items-start gap-3">
                          <div className="rounded-lg bg-gray-200 p-2 dark:bg-slate-700">
                            <Users className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Target Audience
                            </dt>
                            <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
                              {viewer.targetAudience}
                            </dd>
                          </div>
                        </div>
                      )}
                      {viewer.level && (
                        <div className="flex items-start gap-3">
                          <div className="rounded-lg bg-gray-200 p-2 dark:bg-slate-700">
                            <GraduationCap className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Level
                            </dt>
                            <dd className="mt-0.5">
                              <span
                                className={cn(
                                  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                                  levelBadgeClasses(viewer.level),
                                )}
                              >
                                {viewer.level}
                              </span>
                            </dd>
                          </div>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {/* Module Guide — minimal numbered module list within the
                    About card (mirrors labs: book icon + heading, then a plain
                    numbered list of module titles). */}
                <div>
                  <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                    <BookOpen className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    Module Guide
                  </h3>
                  <ol className="mt-3 space-y-1">
                    {viewer.modules.map((module, index) => (
                      <li key={module.uuid}>
                        <button
                          className={cn(
                            "flex w-full items-center gap-3 py-1.5 text-left transition-colors",
                            !active && "cursor-not-allowed opacity-60",
                          )}
                          disabled={!active}
                          onClick={() => goToPlayer(module.uuid)}
                          type="button"
                        >
                          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                            {index + 1}
                          </span>
                          <span className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-white">
                            {module.title}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Footer button — inside the About card. */}
                {active && (
                  <div className="flex items-center justify-end border-t border-gray-200 pt-6 dark:border-slate-700">
                    <Button onClick={() => goToPlayer(resumeModuleUuid)}>
                      Start Learning
                      <Play className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Course Progress sidebar */}
          <div className="space-y-4">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 lg:sticky lg:top-6">
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 p-5 dark:border-slate-700 dark:from-slate-800 dark:to-slate-700/60">
                <div className="rounded-lg bg-slate-600 p-2">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Course Progress
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {completedCount} of {totalModules}{" "}
                    {totalModules === 1 ? "module" : "modules"} completed
                  </p>
                </div>
              </div>
              <div className="p-5">
                <Progress className="h-2" value={active ? percent : 0} />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {active ? `${percent}% complete` : "Enroll to track progress"}
                </p>

                <ol className="mt-4 space-y-1">
                  {viewer.modules.map((module, index) => {
                    const isComplete = statusMap[module.uuid] === "completed";
                    const isCurrent =
                      active && resumeModuleUuid === module.uuid && !isComplete;
                    return (
                      <li key={module.uuid}>
                        <button
                          className={cn(
                            "flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors",
                            isCurrent
                              ? "bg-primary/5 dark:bg-primary/10"
                              : "hover:bg-gray-50 dark:hover:bg-slate-700/50",
                            !active && "cursor-not-allowed opacity-60",
                          )}
                          disabled={!active}
                          onClick={() => goToPlayer(module.uuid)}
                          type="button"
                        >
                          <span className="mt-0.5 flex-shrink-0">
                            {isComplete ? (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500">
                                <CheckCircle2 className="h-4 w-4 text-white" />
                              </span>
                            ) : (
                              <span
                                className={cn(
                                  "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                                  isCurrent
                                    ? "bg-primary text-white"
                                    : "bg-gray-200 text-gray-600 dark:bg-slate-600 dark:text-gray-300",
                                )}
                              >
                                {index + 1}
                              </span>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className={cn(
                                "block truncate text-sm",
                                isCurrent
                                  ? "font-medium text-primary"
                                  : "text-gray-900 dark:text-white",
                              )}
                            >
                              {module.title}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {blockTypeLabel(module)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        cancelMessage="Keep enrollment"
        confirmMessage="Unenroll"
        dialogInstructionFinalQuestion="Are you sure you want to unenroll?"
        dialogInstructionItems={[
          "Your progress is preserved.",
          "You can re-enroll later to pick up where you left off.",
        ]}
        dialogInstructionTitle="Unenrolling from this course will:"
        dialogTitleContent="Unenroll from course"
        isOpen={confirmUnenrollOpen}
        onClose={() => setConfirmUnenrollOpen(false)}
        onConfirm={() => {
          setConfirmUnenrollOpen(false);
          void unenroll();
        }}
      />
    </div>
  );
}

export default CourseDetail;
